import { Alert, Badge, Box, Card, ScrollArea, Stack, Table, Text } from "@mantine/core";
import CPTSectionHeader from "./CPTSectionHeader";
import { IconInfoCircle } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  CPTIndexKey,
  CPTSeverity,
  INDEX_LABEL_KEY,
  SEVERITY_BADGE_KEY,
  SEVERITY_MANTINE_COLOR,
} from "../utils/CPTAssessmentUtils";
import {
  StageBucket,
  StageMatrix,
  StageMetrics,
  buildStageBuckets,
  computeStageMatrix,
  resolveStageMetrics,
  renderStageDisplayName,
} from "../utils/cptStageMetrics";
import type {
  ContinuousPerformanceStageEvent,
  CPTStageScoringResult,
  HeadVelocity,
  Stimulus,
  StimulusResponse,
} from "types/tests";

interface CPTStageBreakdownSectionProps {
  stageEvents?: ContinuousPerformanceStageEvent[];
  stimuli?: Stimulus[] | null;
  responses?: StimulusResponse[] | null;
  headVelocityData?: HeadVelocity[];
  backendStages?: CPTStageScoringResult[] | null;
  fallbackTestEndSec?: number;
  sectionNumber?: number;
}

const INDEX_ORDER: CPTIndexKey[] = [
  "attentiveness",
  "timeliness",
  "impulsiveness",
  "hyperReactivity",
];

function formatRatio(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatMs(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${Math.round(value)} ms`;
}

function formatVelocity(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return (value * 100).toFixed(1);
}

function formatDeviation(deviation: number | null): string {
  if (deviation === null || !Number.isFinite(deviation)) return "—";
  const sign = deviation > 0 ? "+" : "";
  return `${sign}${(deviation * 100).toFixed(0)}`;
}

const StageMetricCell: React.FC<{
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  emphasis?: boolean;
}> = ({ primary, secondary, emphasis }) => (
  <Table.Td style={{ verticalAlign: "top" }}>
    <Stack gap={0}>
      <Text size="sm" fw={emphasis ? 600 : undefined} lh="20px">
        {primary}
      </Text>
      <Text size="xs" c="dimmed" lh="14px">
        {secondary ?? " "}
      </Text>
    </Stack>
  </Table.Td>
);

const StageBreakdownRow: React.FC<{ metrics: StageMetrics; t: TFunction }> = ({
  metrics: m,
  t,
}) => {
  const ns = "extraTests.continuousPerformance.result.stageBreakdown";
  return (
    <Table.Tr>
      <StageMetricCell
        emphasis
        primary={renderStageDisplayName(m.stage, t)}
        secondary={`${m.stage.shortLabel} · ${m.stage.rawName}`}
      />
      <StageMetricCell primary={formatDuration(m.stage.durationSec)} />
      <StageMetricCell primary={m.nTargets} />
      <StageMetricCell
        primary={m.nHits}
        secondary={m.hitRate !== null ? formatRatio(m.hitRate) : undefined}
      />
      <StageMetricCell primary={m.nMisses} />
      <StageMetricCell
        primary={m.nFalseAlarms}
        secondary={
          m.falseAlarmRate !== null
            ? t(`${ns}.falseAlarmRateOf`, {
                rate: formatRatio(m.falseAlarmRate),
                count: m.nNonTargets,
              })
            : undefined
        }
      />
      <StageMetricCell primary={m.nMultiPress} />
      <StageMetricCell primary={formatMs(m.rtMeanMs)} />
      <StageMetricCell primary={formatVelocity(m.headVelocityMean)} />
    </Table.Tr>
  );
};

const StageBreakdownTable: React.FC<{ metrics: StageMetrics[]; t: TFunction }> = ({
  metrics,
  t,
}) => {
  const ns = "extraTests.continuousPerformance.result.stageBreakdown";
  return (
    <Card withBorder radius="md" p="lg">
      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1} mb="xs">
        {t(`${ns}.cardEyebrow`)}
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        {t(`${ns}.description`)}
      </Text>
      <ScrollArea>
        <Table striped withTableBorder withColumnBorders verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t(`${ns}.headers.stage`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.duration`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.targets`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.hits`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.misses`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.falseAlarms`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.extraPresses`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.meanRt`)}</Table.Th>
              <Table.Th>{t(`${ns}.headers.meanHeadV`)}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {metrics.map((m) => (
              <StageBreakdownRow key={m.stage.key} metrics={m} t={t} />
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Card>
  );
};

const StageSeverityMatrix: React.FC<{
  buckets: StageBucket[];
  matrix: StageMatrix;
  baselineLabel: string | null;
  t: TFunction;
}> = ({ buckets, matrix, baselineLabel, t }) => {
  const ns = "extraTests.continuousPerformance.result.severityMatrix";
  return (
    <Card withBorder radius="md" p="lg">
      <Alert icon={<IconInfoCircle size={18} />} color="yellow" variant="light" radius="md" mb="md">
        <Text size="sm" fw={600} mb={4}>
          {t(`${ns}.placeholderHeading`)}
        </Text>
        <Text size="xs" lh={1.5}>
          {baselineLabel
            ? t(`${ns}.placeholderBodyWithBaseline`, { baselineLabel })
            : t(`${ns}.placeholderBody`)}
        </Text>
      </Alert>

      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1} mb="xs">
        {t(`${ns}.cardEyebrow`)}
      </Text>
      <ScrollArea>
        <Table withTableBorder withColumnBorders verticalSpacing="sm" layout="auto">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                {t("extraTests.continuousPerformance.result.numericSummary.headers.index")}
              </Table.Th>
              {buckets.map((b) => (
                <Table.Th key={b.key} ta="center" style={{ whiteSpace: "nowrap" }}>
                  <Stack gap={0} align="center">
                    <Text size="sm" fw={700}>
                      {b.shortLabel}
                    </Text>
                    <Text size="10px" c="dimmed">
                      {renderStageDisplayName(b, t)}
                    </Text>
                  </Stack>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {INDEX_ORDER.map((key) => (
              <Table.Tr key={key}>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {t(INDEX_LABEL_KEY[key])}
                  </Text>
                </Table.Td>
                {buckets.map((b) => {
                  const cell = matrix.cellsByStage[b.key]?.[key];
                  const severity: CPTSeverity = cell?.severity ?? "inconclusive";
                  const color = SEVERITY_MANTINE_COLOR[severity];
                  const isBaseline = b.key === matrix.baselineKey;
                  const hasScore = cell?.score !== null && cell?.score !== undefined;
                  const hasDeviation =
                    !isBaseline && cell?.deviation !== null && cell?.deviation !== undefined;
                  return (
                    <Table.Td
                      key={b.key}
                      bg={`${color}.0`}
                      ta="center"
                      style={{ verticalAlign: "top" }}
                    >
                      <Stack gap={4} align="center">
                        <Text
                          size="sm"
                          fw={700}
                          c={severity === "expected" ? `${color}.9` : `${color}.8`}
                          mih={20}
                          lh="20px"
                        >
                          {hasScore ? formatRatio(cell?.score ?? null, 0) : "—"}
                        </Text>
                        <Badge
                          color={color}
                          size="xs"
                          radius="sm"
                          w={88}
                          h={18}
                          style={{ flexShrink: 0 }}
                        >
                          {isBaseline && hasScore
                            ? t(`${ns}.cell.baseline`)
                            : t(SEVERITY_BADGE_KEY[severity])}
                        </Badge>
                        <Text size="10px" c="dimmed" mih={14} lh="14px">
                          {hasDeviation
                            ? t(`${ns}.cell.deviationSuffix`, {
                                deviation: formatDeviation(cell?.deviation ?? null),
                              })
                            : " "}
                        </Text>
                      </Stack>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Card>
  );
};

const CPTStageBreakdownSection: React.FC<CPTStageBreakdownSectionProps> = ({
  stageEvents,
  stimuli,
  responses,
  headVelocityData,
  backendStages,
  fallbackTestEndSec,
  sectionNumber,
}) => {
  const { t } = useTranslation();
  const buckets = useMemo(
    () => buildStageBuckets(stageEvents, fallbackTestEndSec),
    [stageEvents, fallbackTestEndSec],
  );
  const metrics = useMemo(
    () => resolveStageMetrics(buckets, stimuli, responses, headVelocityData, backendStages),
    [buckets, stimuli, responses, headVelocityData, backendStages],
  );

  if (!buckets.length) {
    return (
      <Box>
        <Card withBorder radius="md" p="xl">
          <Text c="dimmed">
            {t("extraTests.continuousPerformance.result.stageBreakdown.noStages")}
          </Text>
        </Card>
      </Box>
    );
  }

  return (
    <Stack gap="xl">
      <CPTSectionHeader
        number={sectionNumber}
        title={t("extraTests.continuousPerformance.result.stageBreakdown.title")}
      />
      <StageBreakdownTable metrics={metrics} t={t} />
    </Stack>
  );
};

interface CPTStageSeverityMatrixSectionProps {
  stageEvents?: ContinuousPerformanceStageEvent[];
  stimuli?: Stimulus[] | null;
  responses?: StimulusResponse[] | null;
  headVelocityData?: HeadVelocity[];
  backendStages?: CPTStageScoringResult[] | null;
  fallbackTestEndSec?: number;
  sectionNumber?: number;
}

export const CPTStageSeverityMatrixSection: React.FC<CPTStageSeverityMatrixSectionProps> = ({
  stageEvents,
  stimuli,
  responses,
  headVelocityData,
  backendStages,
  fallbackTestEndSec,
  sectionNumber,
}) => {
  const { t } = useTranslation();
  const buckets = useMemo(
    () => buildStageBuckets(stageEvents, fallbackTestEndSec),
    [stageEvents, fallbackTestEndSec],
  );
  const metrics = useMemo(
    () => resolveStageMetrics(buckets, stimuli, responses, headVelocityData, backendStages),
    [buckets, stimuli, responses, headVelocityData, backendStages],
  );
  const matrix = useMemo(() => computeStageMatrix(metrics), [metrics]);
  const baselineLabel = useMemo(() => {
    if (!matrix.baselineKey) return null;
    const base = buckets.find((b) => b.key === matrix.baselineKey);
    return base ? renderStageDisplayName(base, t) : null;
  }, [buckets, matrix.baselineKey, t]);

  if (!buckets.length) return null;

  return (
    <Stack gap="xl">
      <CPTSectionHeader
        number={sectionNumber}
        title={t("extraTests.continuousPerformance.result.severityMatrix.title")}
      />
      <StageSeverityMatrix buckets={buckets} matrix={matrix} baselineLabel={baselineLabel} t={t} />
    </Stack>
  );
};

export default CPTStageBreakdownSection;
